import { TextField, useSnackbarsPipeline } from "@bonliva-crm/ui";
import { useLocale } from "@hooks";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";
import { memo, useEffect } from "react";
import { useUpdateConsultantQualityNoteMutation } from "@graphql";

export type QualityNoteModalData = {
  consultantId: string;
  qualityNote: string;
};

type Props = {
  data?: QualityNoteModalData;
  onClose: () => void;
};

export const QualityNoteModal = memo((props: Props) => {
  const { locale } = useLocale();
  const classes = useStyles();
  const { showError, showSuccess } = useSnackbarsPipeline();

  const {
    formState: { isValid, isDirty, isSubmitting, errors },
    reset,
    handleSubmit,
    control,
    getValues
  } = useForm<{
    qualityNote: string;
  }>({
    defaultValues: {
      qualityNote: props.data?.qualityNote
    },
    mode: "onChange"
  });

  useEffect(() => {
    reset(props.data);
  }, [props.data?.qualityNote]);

  const [mutation] = useUpdateConsultantQualityNoteMutation();

  const open = !!props.data;
  const maxLength = 100;

  const onSubmit = () => {
    return mutation({
      variables: {
        consultantId: props.data?.consultantId,
        note: getValues().qualityNote
      },
      onCompleted: () => {
        props.onClose();
        showSuccess(locale.Saved);
      },
      onError: () => {
        showError(locale.cannotPerformAction);
      }
    });
  };

  return (
    <Dialog open={open} onClose={props.onClose}>
      <DialogTitle>{locale.updateQualityNote}</DialogTitle>
      <DialogContent className={classes.root}>
        <form id={"qualityNoteForm"} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name={"qualityNote"}
            control={control}
            rules={{ maxLength }}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                fullWidth
                minRows={2}
                showWordCount
                inputProps={{ maxLength }}
                label={locale.TypeHere}
                error={!!errors.qualityNote}
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={props.onClose}>
          {locale.Cancel}
        </Button>
        <Button
          type={"submit"}
          form={"qualityNoteForm"}
          variant="contained"
          color="primary"
          disabled={!isDirty || !isValid || isSubmitting}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const useStyles = makeStyles(() => ({
  root: {
    minWidth: 500
  }
}));
